import React, { useEffect, useState } from "react";
import { useRebaseClient } from "@rebasepro/app";
import { useSidePanel } from "@rebasepro/cms";
import type { RebaseClient } from "@rebasepro/types";
import type { EntityCustomViewParams } from "@rebasepro/cms-types";
import {
    Button as RebaseButton,
    Typography as RebaseTypography
} from "@rebasepro/ui";

import { PersonAvatar } from "./_avatar";

const Button = RebaseButton as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;

type TalentRow = {
    id: string;
    full_name?: string | null;
    photo_url?: string | null;
    email?: string | null;
    location?: string | null;
    lead_status?: string | null;
    questionnaire_completed_at?: string | null;
};

/**
 * "Candidato" on a user record — the other end of a link that only ever read
 * one way.
 *
 * `talents.auth_user_id` points at `rebase.users`, so from a candidate you can
 * see the account. From the account there was nothing: the users collection
 * has no column pointing back, and no relation property can invent one. This
 * runs the reverse query instead — the same lookup
 * `talent_applications.beforeSave` does — and gives the operator the row.
 *
 * The three states are deliberately distinct, because they mean different
 * things about a person:
 *
 *   • a candidate, opened from here;
 *   • an account with no candidate profile — a company user, an admin, or a
 *     registration that stopped halfway. Not an error, and it must not read as
 *     one;
 *   • a failed lookup, which is *not* the same as "no profile" and says so.
 *     An empty result and a broken query rendering identically is the failure
 *     this product has decided, repeatedly, not to ship.
 */
export function UserTalentPanel({ entity, formContext }: EntityCustomViewParams) {
    const client = useRebaseClient<RebaseClient>();
    const sidePanel = useSidePanel();

    const rawId = entity?.id ?? formContext?.entityId;
    const userId = rawId ? String(rawId) : null;

    const [talent, setTalent] = useState<TalentRow | null>(null);
    const [state, setState] = useState<"loading" | "ready" | "error">("loading");
    const [error, setError] = useState("");

    useEffect(() => {
        const data = (client as any)?.data;
        if (!data || !userId) return;
        let cancelled = false;

        (async () => {
            setState("loading");
            try {
                const res = await data.collection("talents").find({
                    where: { auth_user_id: ["==", userId] },
                    limit: 1
                });
                if (cancelled) return;
                setTalent(((res?.data ?? [])[0] as TalentRow) ?? null);
                setState("ready");
            } catch (err: any) {
                if (cancelled) return;
                setError(err?.message || "No se pudo consultar el perfil de candidato");
                setState("error");
            }
        })();

        return () => { cancelled = true; };
    }, [client, userId]);

    if (state === "loading") {
        return <div className="p-6"><Typography variant="body1">Buscando el perfil…</Typography></div>;
    }

    if (state === "error") {
        return (
            <div className="p-6">
                <Typography variant="body1" color="error">{error}</Typography>
                <Typography variant="body2" className="mt-1 block text-text-secondary">
                    Esto no significa que la persona no tenga perfil: la consulta falló.
                </Typography>
            </div>
        );
    }

    if (!talent) {
        return (
            <div className="p-6">
                <Typography variant="body1">Esta cuenta no tiene perfil de candidato.</Typography>
                <Typography variant="body2" className="mt-1 block text-text-secondary">
                    Normal en cuentas de empresa y de administración, y en registros que quedaron a medias.
                </Typography>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 p-6">
            <div className="flex items-start gap-3">
                <PersonAvatar
                    name={talent.full_name || talent.email}
                    photoKey={talent.photo_url}
                    size={40}
                />
                <div className="min-w-0">
                    <Typography variant="subtitle2" component="h3">{talent.full_name || talent.email || "(sin nombre)"}</Typography>
                    <Typography variant="body2" className="text-text-secondary">
                        {[talent.email, talent.location].filter(Boolean).join(" · ") || "—"}
                    </Typography>
                </div>
            </div>
            <Typography variant="body2" className="text-text-secondary">
                {talent.questionnaire_completed_at
                    ? `Cuestionario completado el ${new Date(talent.questionnaire_completed_at).toLocaleDateString("es-MX")}`
                    : "Cuestionario sin terminar"}
            </Typography>
            <div>
                <Button
                    size="small"
                    onClick={() => sidePanel?.open?.({ path: "talents", entityId: String(talent.id) })}
                >
                    Abrir ficha del candidato
                </Button>
            </div>
        </div>
    );
}
